import * as Sentry from '@sentry/react';

interface SentryUserContext {
  id?: string | null;
  email?: string | null;
  username?: string | null;
  companyId?: string | null;
  role?: string | null;
  module?: string;
}

declare const __SENTRY_RELEASE__: string;
declare const __SENTRY_ENVIRONMENT__: string;

const SENSITIVE_KEYS = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'access_token',
  'access-token',
  'refresh_token',
  'refresh-token',
  'refreshToken',
  'token',
  'jwt',
  'password',
  'secret',
  'apiKey',
  'api_key',
];
const FILTERED = '[Filtered]';

const isSensitiveKey = (key: string) => {
  const normalizedKey = key.toLowerCase();
  return SENSITIVE_KEYS.some((sensitiveKey) => normalizedKey.includes(sensitiveKey.toLowerCase()));
};

export const sanitizeUrl = (value?: unknown) => {
  if (typeof value !== 'string' || !value) return undefined;

  try {
    const parsedUrl = new URL(value, window.location.origin);
    parsedUrl.searchParams.forEach((_, key) => {
      if (isSensitiveKey(key)) parsedUrl.searchParams.set(key, FILTERED);
    });

    parsedUrl.pathname = parsedUrl.pathname
      .split('/')
      .map((segment) => {
        if (/^eyJ[\w-]+\.[\w-]+\.[\w-]+$/.test(segment)) return FILTERED;
        if (/^[A-Za-z0-9_-]{32,}$/.test(segment)) return FILTERED;
        return segment;
      })
      .join('/');

    return parsedUrl.origin === window.location.origin
      ? `${parsedUrl.pathname}${parsedUrl.search}`
      : `${parsedUrl.origin}${parsedUrl.pathname}${parsedUrl.search}`;
  } catch {
    return value.replace(/([?&][^=]*(token|password|secret|key)[^=]*=)[^&]+/gi, `$1${FILTERED}`);
  }
};

const sanitizeHeaders = (headers?: Record<string, string>) => {
  if (!headers) return undefined;

  return Object.entries(headers).reduce<Record<string, string>>((safeHeaders, [key, value]) => {
    safeHeaders[key] = isSensitiveKey(key) ? FILTERED : value;
    return safeHeaders;
  }, {});
};

const sanitizeBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  if (!breadcrumb.data) return breadcrumb;

  const safeData = Object.entries(breadcrumb.data).reduce<Record<string, unknown>>((data, [key, value]) => {
    if (isSensitiveKey(key)) {
      data[key] = FILTERED;
      return data;
    }

    data[key] = key.toLowerCase().includes('url') ? sanitizeUrl(value) : value;
    return data;
  }, {});

  return {
    ...breadcrumb,
    data: safeData,
  };
};

const sanitizeEventRequest = (event: Sentry.ErrorEvent) => {
  const request = event.request;
  if (!request) return event;

  event.request = {
    ...request,
    url: sanitizeUrl(request.url),
    headers: sanitizeHeaders(request.headers),
    cookies: undefined,
    data: undefined,
  };

  if (request.query_string) {
    event.request.query_string = sanitizeUrl(`/?${request.query_string}`)?.replace('/?', '');
  }

  event.breadcrumbs = event.breadcrumbs?.map(sanitizeBreadcrumb);

  return event;
};

const getSampleRate = () => {
  const configuredSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE);
  if (Number.isFinite(configuredSampleRate) && configuredSampleRate >= 0 && configuredSampleRate <= 1) {
    return configuredSampleRate;
  }

  return import.meta.env.PROD ? 0.1 : 0;
};

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT || __SENTRY_ENVIRONMENT__ || import.meta.env.MODE;
const sentryRelease = __SENTRY_RELEASE__ || undefined;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    release: sentryRelease,
    tracesSampleRate: getSampleRate(),
    sendDefaultPii: false,
    beforeBreadcrumb(breadcrumb) {
      return sanitizeBreadcrumb(breadcrumb);
    },
    beforeSend(event) {
      return sanitizeEventRequest(event);
    },
  });
}

export const setSentryUserContext = ({ id, email, username, companyId, role, module = 'web' }: SentryUserContext) => {
  Sentry.setUser(
    id || email || username ? { id: id || undefined, email: email || undefined, username: username || undefined } : null,
  );
  Sentry.setTag('companyId', companyId || 'none');
  Sentry.setTag('user_role', role || 'unknown');
  Sentry.setTag('application_module', module);
};

export const clearSentryUserContext = () => {
  Sentry.setUser(null);
  Sentry.setTag('companyId', 'none');
  Sentry.setTag('user_role', 'anonymous');
  Sentry.setTag('application_module', 'web');
};

export { Sentry };
